import React from "react";
import { Stack } from "react-bootstrap";
import BUTTON from "../../button/Button.component";
import ProjectCardStyles from "./ProjectCard.styles";
import { PackageSummary } from "../../../api/models";
import { useHistory } from "react-router-dom";
import PACKAGE_ITEM from "../PackageSelector/renderer/PackageItem.renderer";
import {
  DefaultProjectCardProps,
  IProjectCardProps,
} from "./ProjectCard.types";
import AppMedia from "../../../theme/utils/AppMedia";
import { toCSSODateString } from "../../../shared/utils/data.utils";
import { AppDispatch, useAppDispatch } from "../../../state-management/store";
import { setSelectedPackage } from "../../../state-management/slices/packageSelector/package-selector.slice";
import CardUtils from "../Card.utils";
import { UseHistoryType } from "../../../shared/types/routing.types";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import {
  EPhaseNames,
  parsePhaseName,
} from "../../../shared/enums/phases.enums";
import { ClickEvent } from "../../../shared/types/event.types";

const { Media } = AppMedia;

const renderPackageList = (
  packageCollection: Array<PackageSummary>,
  projectId: string,
  history: UseHistoryType,
  dispatch: AppDispatch
): React.ReactNode => {
  return packageCollection.map((packageItem: PackageSummary) => {
    const sendDataToGTM = useGTMDispatch();

    const isCompleted =
      parsePhaseName(packageItem?.phase?.id ?? "") === EPhaseNames.COMPLETED;

    return (
      <PACKAGE_ITEM
        key={`${packageItem.packageName}`}
        packageItem={packageItem}
        selected={false}
        disabled={isCompleted}
        onClick={
          /* istanbul ignore next */ (event?: ClickEvent) => {
            if (isCompleted) {
              event?.preventDefault();
              return;
            }

            dispatch(setSelectedPackage(packageItem));
            history.push(
              `${history.location.pathname}project-details/${projectId}/?packageId=${packageItem.packageId}`
            );
            sendDataToGTM({
              event: "eventTracking",
              category: "Construction",
              action: "Click - View Projects",
              label: `Package selected - Package Name: ${packageItem.packageName}`,
            });
          }
        }
      />
    );
  });
};

const PROJECT_CARD: React.FC<IProjectCardProps> = (
  props: IProjectCardProps
) => {
  const { project } = props;
  const { packages, totalPackages } = props.project;

  const sendDataToGTM = useGTMDispatch();
  const dispatch = useAppDispatch();
  const history = useHistory();

  return (
    <ProjectCardStyles.ProjectCard
      className="project-card"
      data-testid="project-card-component"
    >
      {/* CARD HEADER */}
      <ProjectCardStyles.ProjectCardHeader
        className="project-card-header"
        onClick={() => {
          sendDataToGTM({
            event: "eventTracking",
            category: "Construction",
            action: "Click - View Projects",
            label: `Project Title - Project Name: ${project.projectTitle}`,
          });
          CardUtils.navigateToUrl(history, project, packages);
        }}
        data-testid="project-card-header"
      >
        <h4>{project.projectTitle}</h4>
      </ProjectCardStyles.ProjectCardHeader>

      {/* CARD BODY */}
      <ProjectCardStyles.ProjectCardBody className="project-card-body">
        <ProjectCardStyles.ProjectDetailsStack
          className="project-details-stack"
          gap={2}
        >
          <ProjectCardStyles.DetailsStackItem className="details-stack-item">
            <Stack gap={0}>
              <span className="details-stack-item-label">
                Project Coordinator
              </span>
              {CardUtils.renderEmailLink(
                project.projectCoordinator?.name,
                project.projectCoordinator?.email,
                project.projectId,
                project.projectTitle,
                sendDataToGTM
              )}
            </Stack>
          </ProjectCardStyles.DetailsStackItem>
          <div className="hr border-1 border-top" />
          <ProjectCardStyles.DetailsStackItem className="details-stack-item">
            <Stack gap={0}>
              <span className="details-stack-item-label">
                Est. Site Ready Date
              </span>
              <span className="details-stack-item-value">
                {toCSSODateString(project.siteReadyDate)}
              </span>
            </Stack>
          </ProjectCardStyles.DetailsStackItem>
          <div className="hr border-1 border-top" />
          <ProjectCardStyles.DetailsStackItem className="details-stack-item">
            <Stack gap={0}>
              <span className="details-stack-item-label">Main Contact</span>
              <span className="details-stack-item-value">
                {project.mainContact}
              </span>
            </Stack>
          </ProjectCardStyles.DetailsStackItem>
          <div className="hr border-1 border-top" />
          <ProjectCardStyles.DetailsStackItem className="details-stack-item">
            <Stack gap={0}>
              <span className="details-stack-item-label">
                Next Scheduled Contact
              </span>
              <span className="details-stack-item-value">
                {project.nextCeContactTime?.timeAmount} Business Day(s)
              </span>
            </Stack>
          </ProjectCardStyles.DetailsStackItem>
        </ProjectCardStyles.ProjectDetailsStack>

        <Media greaterThanOrEqual="tablet">
          <ProjectCardStyles.PackageListContainer className="package-list-container desktop">
            <span className="package-list-label">
              Showing {packages?.length} of {totalPackages} package(s)
            </span>
            <Stack gap={2}>
              {packages &&
                packages.length &&
                renderPackageList(
                  packages,
                  project?.projectId ?? "",
                  history,
                  dispatch
                )}
            </Stack>
          </ProjectCardStyles.PackageListContainer>
        </Media>

        <Media lessThan="tablet">
          <ProjectCardStyles.PackageListContainer className="package-list-container mobile">
            <span className="package-list-label">
              Showing {packages?.length} of {totalPackages} package(s)
            </span>
            <Stack gap={2}>
              {packages &&
                packages.length &&
                renderPackageList(
                  packages,
                  project?.projectId ?? "",
                  history,
                  dispatch
                )}
            </Stack>
          </ProjectCardStyles.PackageListContainer>
        </Media>
      </ProjectCardStyles.ProjectCardBody>

      {/* CARD FOOTER */}
      <ProjectCardStyles.ProjectCardFooter className="project-card-footer">
        <BUTTON
          variant="secondary"
          className="blue-theme no-scale"
          onClick={
            /* istanbul ignore next */ () => {
              sendDataToGTM({
                event: "eventTracking",
                category: "Construction",
                action: "Click - View Projects",
                label: `View Project button - Project Name: ${project.projectTitle}`,
              });
              CardUtils.navigateToUrl(history, project, packages);
            }
          }
        >
          View Project
        </BUTTON>
      </ProjectCardStyles.ProjectCardFooter>
    </ProjectCardStyles.ProjectCard>
  );
};

PROJECT_CARD.defaultProps = DefaultProjectCardProps;

export default PROJECT_CARD;
